// Clickable field-path chips, shared between the Test dialog (copy) and the
// input-data panel (insert at the caret of the last-focused field).

import { FC, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DoneIcon from '@mui/icons-material/Done'
import { xxllncColor } from '../../../layout/theme/color'

export type FieldPickResult = 'inserted' | 'copied' | false

interface FieldChipsProps {
  fields: string[]
  expressionFor: (field: string) => string
  onPick: (field: string, expression: string) => FieldPickResult
  pickedTooltip: (result: Exclude<FieldPickResult, false>) => string
  action?: 'insert' | 'copy'
}

const FieldChips: FC<FieldChipsProps> = ({
  fields,
  expressionFor,
  onPick,
  pickedTooltip,
  action = 'copy',
}) => {
  const [picked, setPicked] = useState<{ field: string; result: 'inserted' | 'copied' } | null>(null)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The flash-reset timer must not fire after unmount.
  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
  }, [])

  const handlePick = (field: string) => {
    const result = onPick(field, expressionFor(field))
    if (!result) return
    setPicked({ field, result })
    if (resetTimer.current) clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setPicked(null), 1500)
  }

  const IdleIcon = action === 'insert' ? AddIcon : ContentCopyIcon

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {fields.map((field) => {
        const isPicked = picked?.field === field
        return (
          <Tooltip
            key={field}
            title={isPicked && picked ? pickedTooltip(picked.result) : expressionFor(field)}
            arrow
            placement="top"
          >
            <Chip
              label={field}
              size="small"
              icon={
                isPicked
                  ? <DoneIcon sx={{ fontSize: 12 }} />
                  : <IdleIcon sx={{ fontSize: 12 }} />
              }
              onClick={() => handlePick(field)}
              // Keep focus (and caret) in the target field while picking.
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                'height': 24,
                'fontSize': '0.72rem',
                'fontWeight': 500,
                'cursor': 'pointer',
                'fontFamily': 'monospace',
                'bgcolor': isPicked ? xxllncColor.opGreen10 : 'transparent',
                'borderColor': isPicked ? xxllncColor.green100 : 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              variant="outlined"
            />
          </Tooltip>
        )
      })}
    </Box>
  )
}

export default FieldChips
